// 3종 마커(elderly/manager/org)의 HTML content와 anchor(앵커 픽셀 오프셋)를 생성한다.
// 네이버 SDK의 Marker는 icon: { content, anchor } 형태로 임의 HTML을 받는다.
// 색은 hex 직접 박는다 — SDK가 마커 content를 다른 컨테이너에 넣을 때 페이지의
// CSS 변수 상속이 보장되지 않으므로, 외부 토큰 의존을 끊어 일관된 색을 보장.
//
// 토큰 hex는 app/assets/stylesheets/design-tokens.css와 sync. 토큰이 바뀌면
// 여기 PALETTE도 같이 갱신해야 한다 (단계 7 정리에서 token 추출 자동화 검토).
//
// 마커는 "모양(kind) + 색(세부 분류) + 픽토그램(GLYPHS)" 세 축으로 구분한다.
// 색만으로는 색각 이상·저채도 배경에서 구분이 무너지고, 예전처럼 한글 1글자
// (병/보/소/복/요)를 넣으면 작은 크기에서 전부 같은 사각형으로 보였다.

import type { EntityKind } from "./types";
import { SUB_AREA_COLORS } from "./pilotBoundary";

const PALETTE = {
  orange: { 300: "#EFB287", 500: "#E8825B", 600: "#D26A44" },
  green:  { 300: "#9BC3A6", 500: "#6FA88F", 600: "#4F8A72" },
  rose:   { 300: "#DC8B82", 500: "#C9594F", 600: "#A8453C" },
  amber:  { 300: "#E5BF72", 500: "#D9A24A", 600: "#B07F2E" },
  sky:    { 300: "#92AAC6", 500: "#5C82A8", 600: "#436A8E" },
  gray:   { 300: "#CBD5E1", 500: "#94A3B8" },
} as const;

type ColorKey = keyof typeof PALETTE;

// 기관 카테고리 → 색. map.tsx의 CAT_COLOR와 동일. 단계 7 통합 정리 예정.
const CATEGORY_COLOR: Record<string, ColorKey> = {
  hospital: "rose",
  health:   "green",
  fire:     "orange",
  welfare:  "sky",
  care:     "amber",
};

// 기관 카테고리 라벨. map.tsx의 CAT_LABEL과 동일(툴팁 표기용).
const CATEGORY_LABEL: Record<string, string> = {
  hospital: "병원", health: "보건", fire: "소방", welfare: "복지", care: "요양",
};

// 어르신 위험등급 → 색. map.tsx의 RISK_COLOR와 동일.
const RISK_COLOR: Record<string, ColorKey> = {
  critical: "rose",
  warning:  "amber",
  stable:   "green",
};

const RISK_LABEL: Record<string, string> = {
  critical: "위험", warning: "주의", stable: "안정",
};

// 하위 구역 색은 pilotBoundary.ts(단일 출처)에서 가져온다 — 지도의 구역 폴리곤·
// 커버리지·복지관 앵커와 어르신 마커의 구역 배지가 같은 색이라야 사용자가 "이
// 어르신은 저 구역"을 한눈에 잇는다. (예전엔 파일마다 hex를 직박아 sync가 깨졌다.)
const SUB_AREA_COLOR = SUB_AREA_COLORS;

// 매니저 상태 도트(우하단 작은 점) 색.
const MANAGER_STATUS_DOT: Record<string, ColorKey> = {
  available:  "green",
  busy:       "amber",
  overloaded: "rose",
  moving:     "orange",
  offline:    "gray",
};

const MANAGER_STATUS_LABEL: Record<string, string> = {
  available: "가용", busy: "바쁨", overloaded: "과부하", moving: "이동중", offline: "오프라인",
};

const CONTRACT_LABEL: Record<string, string> = {
  completed: "협약 완료", reviewing: "검토중", linked: "연동중", none: "미체결",
};

const CONTRACT_COLOR: Record<string, ColorKey> = {
  completed: "green", reviewing: "amber", linked: "green", none: "rose",
};

const KIND_LABEL: Record<EntityKind, string> = {
  elderly: "어르신", manager: "케어매니저", org: "유관기관",
};

// ── 픽토그램 ────────────────────────────────────────────────────────
//
// 모두 24×24 viewBox 기준 실루엣. fg는 픽토그램 색, bg는 마커 배경색으로
// "파낸" 부분(방패 안 십자, 불꽃 안쪽 심지)에 쓴다 — mask/clip 없이 배경색
// 도형을 덮어 그려 SDK 컨테이너 환경에 의존하지 않게 한다.
export type GlyphKey = "elderly" | "manager" | "hospital" | "health" | "fire" | "welfare" | "care";

const GLYPHS: Record<GlyphKey, (fg: string, bg: string) => string> = {
  // 지팡이 짚은 사람 — 어르신.
  elderly: (fg) => `<g fill="${fg}">` +
    `<circle cx="9.8" cy="6.4" r="3.5"/>` +
    `<path d="M2.6 21c.4-4 3.3-6.6 7.2-6.6S16.6 17 17 21z"/>` +
    `<circle cx="19.35" cy="7.6" r="1.75"/>` +
    `<rect x="18.4" y="8.6" width="1.9" height="12.4" rx=".95"/>` +
    `</g>`,
  // 사람 — 케어매니저(어르신과 달리 지팡이 없음 + 어깨가 넓다).
  manager: (fg) => `<g fill="${fg}">` +
    `<circle cx="12" cy="7.3" r="4"/>` +
    `<path d="M3.7 21c.5-4.2 4-6.9 8.3-6.9s7.8 2.7 8.3 6.9z"/>` +
    `</g>`,
  // 십자 — 병원·의원.
  hospital: (fg) => `<path fill="${fg}" d="M9.6 2.8h4.8a1.2 1.2 0 0 1 1.2 1.2v4.4h4.4a1.2 1.2 0 0 1 1.2 1.2v4.8a1.2 1.2 0 0 1-1.2 1.2h-4.4v4.4a1.2 1.2 0 0 1-1.2 1.2H9.6a1.2 1.2 0 0 1-1.2-1.2v-4.4H4a1.2 1.2 0 0 1-1.2-1.2V9.6A1.2 1.2 0 0 1 4 8.4h4.4V4a1.2 1.2 0 0 1 1.2-1.2Z"/>`,
  // 방패 + 십자 — 보건소·보건지소(공공 보건).
  health: (fg, bg) =>
    `<path fill="${fg}" d="M12 2.2 4.4 5.2v5.9c0 4.6 3.1 8.9 7.6 10.7 4.5-1.8 7.6-6.1 7.6-10.7V5.2Z"/>` +
    `<path fill="${bg}" d="M10.9 6.9h2.2v2.6h2.6v2.2h-2.6v2.6h-2.2v-2.6H8.3V9.5h2.6z"/>`,
  // 불꽃 — 소방·119.
  fire: (fg, bg) =>
    `<path fill="${fg}" d="M12 2.4c3.7 3.5 6.2 6.6 6.2 10.1a6.2 6.2 0 1 1-12.4 0c0-3.5 2.5-6.6 6.2-10.1Z"/>` +
    `<path fill="${bg}" d="M12 12.2c1.8 1.7 3 3.2 3 4.9a3 3 0 1 1-6 0c0-1.7 1.2-3.2 3-4.9Z"/>`,
  // 하트 — 복지관·주민센터.
  welfare: (fg) => `<path fill="${fg}" d="M12 20.8C5.4 16.2 2.6 13 2.6 9.9a5.2 5.2 0 0 1 9.4-3 5.2 5.2 0 0 1 9.4 3c0 3.1-2.8 6.3-9.4 10.9Z"/>`,
  // 집 — 요양기관(생활 시설).
  care: (fg) => `<path fill="${fg}" d="M12 2.6 2 10.4H5V21h5.2v-5.6h3.6V21H19V10.4h3Z"/>`,
};

const CATEGORY_GLYPH: Record<string, GlyphKey> = {
  hospital: "hospital", health: "health", fire: "fire", welfare: "welfare", care: "care",
};

// 픽토그램의 알맹이(24×24 좌표계 도형)만. 이미 <svg> 안에 있는 곳(핀 내부,
// 범례의 핀 축소판)이 <g transform>으로 배치해 쓴다. 중첩 <svg>를 만들지 않는
// 게 요점 — 중첩 svg도 전역 `.app svg{width:1em}` 규칙에 매치된다.
export function glyphMarkup(key: GlyphKey, fg = "#fff", bg = "transparent"): string {
  return GLYPHS[key](fg, bg);
}

// 픽토그램을 독립 <svg> 문자열로. 범례(map.tsx / landing-desktop.tsx)가 마커와
// 똑같은 그림을 쓰도록 공개한다 — 범례와 지도가 어긋나면 구분 자체가 무의미해진다.
//
// ⚠️ width/height는 반드시 인라인 style로. 전역 `.app svg{width:1em;height:1em}`가
// 속성만 준 경우를 덮어쓴다(아래 elderlyStyle 주석 참고).
export function glyphSvg(
  key: GlyphKey,
  { size = 14, fg = "#fff", bg = "transparent" }: { size?: number; fg?: string; bg?: string } = {},
): string {
  return `<svg viewBox="0 0 24 24" width="${size}" height="${size}" aria-hidden="true"` +
    ` style="display:block;width:${size}px;height:${size}px;">${GLYPHS[key](fg, bg)}</svg>`;
}

// 범례가 마커와 같은 색을 쓰도록 공개하는 hex 표. 토큰 var()가 아니라 hex인
// 이유는 위 PALETTE 주석과 동일(마커 content는 CSS 변수 상속이 보장 안 됨).
export const MARKER_HEX = {
  manager: PALETTE.orange[500],
  risk: {
    critical: PALETTE.rose[500], warning: PALETTE.amber[500], stable: PALETTE.green[500],
  } as Record<string, string>,
  category: {
    hospital: PALETTE.rose[500], health: PALETTE.green[500], fire: PALETTE.orange[500],
    welfare: PALETTE.sky[500], care: PALETTE.amber[500],
  } as Record<string, string>,
};

export interface MarkerSpec {
  dim?: boolean;
  selected?: boolean;
  // 대시보드 미니맵처럼 좁은 카드에 쓰는 축소 버전. 모양·색·픽토그램은 그대로 두고
  // 크기만 줄인다(풀 지도와 같은 그림이어야 사용자가 두 화면을 이어서 읽는다).
  compact?: boolean;
  // 툴팁 맨 앞에 붙일 상황 배지(예: "다음 방문"). 화면 고유의 맥락을 마커 모양을
  // 건드리지 않고 얹는 용도.
  note?: string;
}

export interface MarkerStyle {
  content: string;
  anchorX: number;
  anchorY: number;
  zIndex: number;
  // 마우스오버 시 띄울 커스텀 툴팁 HTML(tooltip.ts가 body에 붙여 렌더).
  // 네이티브 title 속성은 쓰지 않는다 — 스타일을 줄 수 없고 지연도 길다.
  tooltip: string;
}

// 외부에서 dim/selected가 바뀌었을 때만 setIcon을 호출하기 위한 비교 헬퍼.
export function specEqual(a: MarkerSpec, b: MarkerSpec): boolean {
  return !!a.dim === !!b.dim && !!a.selected === !!b.selected && !!a.compact === !!b.compact;
}

// 진입점. 호출자가 kind와 item, opts를 넘기면 SDK에 그대로 넘길 수 있는
// content/anchor/zIndex/tooltip을 돌려준다.
export function markerStyle(kind: EntityKind, item: any, opts: MarkerSpec = {}): MarkerStyle {
  switch (kind) {
    case "elderly": return elderlyStyle(item, opts);
    case "manager": return managerStyle(item, opts);
    case "org":     return orgStyle(item, opts);
  }
}

// 어르신 — 핀(물방울) 모양 + 지팡이 짚은 사람 픽토그램. 위험등급 색.
function elderlyStyle(e: any, opts: MarkerSpec): MarkerStyle {
  const colorKey = RISK_COLOR[e.risk_level] ?? "green";
  const color = PALETTE[colorKey][500];
  // 선택된 마커는 검색어 미매치라도 dim시키지 않는다 — "지금 선택된 대상"이
  // 흐릿해지면 사용자가 자기가 어디를 보고 있었는지 놓친다.
  const opacity = opts.dim && !opts.selected ? DIM_OPACITY : 1;
  const ring = opts.selected ? SELECTED_RING : "none";
  const z = opts.selected ? Z_SELECTED : Z_ELDERLY;

  // SVG 안에 핀 path. 어르신은 지도의 주 관심 대상이므로 다른 마커(매니저/기관)보다
  // 눈에 띄게 한다: 핀 머리 원 지름을 기본 클러스터 크기(clusterSize count<10 = 36px)에
  // 맞춰 개별 마커와 소규모 클러스터가 같은 스케일로 보이게 한다(핀은 꼬리가 있어 높이는
  // viewBox 비율대로 36×46). + SVG drop-shadow(핀 모양대로 그림자, 매니저/기관은
  // box-shadow가 있는데 어르신만 없어 평평하게 묻히던 것 해소) + 두꺼운 흰 테두리(2.5)로
  // 채도 낮은 안정(녹색)도 배경에서 분리. anchor는 핀 끝(아래 중앙) = (18, 46).
  // viewBox는 0 0 28 36 그대로 두고 width/height만 키워 path가 비례 확대되게 한다.
  // 픽토그램은 <g transform>으로 핀 머리 원 중심(14,14)에 앉힌다. 중첩 <svg>를 쓰면
  // 아래 전역 CSS 함정에 그대로 걸리므로(중첩 svg도 `.app svg` 매치) 쓰지 않는다.
  //
  // ⚠️ svg 크기는 반드시 인라인 style(width/height)로 준다. 전역 CSS
  // `.app svg{width:1em;height:1em}`(design-tokens.css)가 width/height "속성"을
  // 덮어써 마커가 ~16px로 고정되는 함정이 있다 — 마커는 .app 안에 렌더되고
  // 어르신만 <svg>라 이 규칙에 걸린다. 인라인 style은 전역 규칙보다 우선하므로
  // 여기서만 크기가 실제 반영된다.
  const w = opts.compact ? 28 : 36;
  const h = opts.compact ? 36 : 46;
  const glyph = GLYPHS.elderly("#fff", color);
  // 권역 배지 — 김해(R018) 어르신만 sub_area 값이 있음. 핀 우상단에 작은 도트.
  // 위험등급(핀 몸통 색)이 1순위 시그널이므로 도트는 크기(10px)를 넘지 않게 두어
  // 보조 라벨로 남겨둔다. dsize=compact 8px / 표준 10px, 흰 링으로 배경 색과 분리.
  const dsize = opts.compact ? 8 : 10;
  const subDot = e.sub_area && SUB_AREA_COLOR[e.sub_area]
    ? `<span style="position:absolute;top:-2px;right:-2px;width:${dsize}px;height:${dsize}px;border-radius:50%;background:${SUB_AREA_COLOR[e.sub_area]};border:2px solid #fff;box-shadow:0 1px 2px rgba(0,0,0,0.28);pointer-events:none;"></span>`
    : "";
  const content = `
    <div style="position:relative;width:${w}px;height:${h}px;opacity:${opacity};${ring}border-radius:${w / 2}px ${w / 2}px ${w / 2}px 6px;transition:opacity 0.15s;cursor:pointer;">
      <svg width="${w}" height="${h}" viewBox="0 0 28 36" style="display:block;width:${w}px;height:${h}px;filter:drop-shadow(0 3px 4px rgba(0,0,0,0.35));">
        <path d="M14 0C6.3 0 0 6.3 0 14c0 10 14 22 14 22s14-12 14-22C28 6.3 21.7 0 14 0Z"
              fill="${color}" stroke="#fff" stroke-width="2.5"/>
        <g transform="translate(7.1,7.1) scale(0.575)" style="pointer-events:none;">${glyph}</g>
      </svg>
      ${subDot}
    </div>
  `;
  return { content, anchorX: w / 2, anchorY: h, zIndex: z, tooltip: elderlyTooltip(e, colorKey, opts.note) };
}

// 매니저 — 원형 그라데이션 + 사람 픽토그램 + 우하단 상태 도트. anchor는 원 중앙 (17, 17).
function managerStyle(m: any, opts: MarkerSpec): MarkerStyle {
  const gradStart = PALETTE.orange[300];
  const gradEnd   = PALETTE.orange[500];
  const dotKey    = MANAGER_STATUS_DOT[m.status] ?? "gray";
  const dot       = PALETTE[dotKey][500];
  const opacity   = opts.dim && !opts.selected ? DIM_OPACITY : 1;
  const ring      = opts.selected ? SELECTED_RING : "box-shadow:0 6px 14px rgba(0,0,0,0.22);";
  const z         = opts.selected ? Z_SELECTED : Z_MANAGER;

  const size = opts.compact ? 26 : 34;
  const dotSize = opts.compact ? 9 : 10;
  const content = `
    <div style="position:relative;width:${size}px;height:${size}px;border-radius:50%;background:linear-gradient(135deg,${gradStart},${gradEnd});display:flex;align-items:center;justify-content:center;border:2.5px solid #fff;${ring}opacity:${opacity};transition:opacity 0.15s;cursor:pointer;">
      ${glyphSvg("manager", { size: opts.compact ? 15 : 19, fg: "#fff", bg: gradEnd })}
      <span style="position:absolute;bottom:-1px;right:-1px;width:${dotSize}px;height:${dotSize}px;border-radius:50%;background:${dot};border:2px solid #fff;"></span>
    </div>
  `;
  const half = Math.round(size / 2);
  return { content, anchorX: half, anchorY: half, zIndex: z, tooltip: managerTooltip(m, dotKey, opts.note) };
}

// 기관 — 둥근 사각. 카테고리 색 + 카테고리 픽토그램. anchor 중앙 (14, 14).
function orgStyle(o: any, opts: MarkerSpec): MarkerStyle {
  const cat   = o.category ?? "";
  const colorKey = CATEGORY_COLOR[cat] ?? "sky";
  const color = PALETTE[colorKey][500];
  const glyph = CATEGORY_GLYPH[cat] ?? "welfare";
  const opacity = opts.dim && !opts.selected ? DIM_OPACITY : 1;
  const ring  = opts.selected ? SELECTED_RING : "box-shadow:0 4px 10px rgba(0,0,0,0.18);";
  const z     = opts.selected ? Z_SELECTED : Z_ORG;

  const size = opts.compact ? 22 : 28;
  const content = `
    <div style="width:${size}px;height:${size}px;border-radius:${opts.compact ? 7 : 8}px;background:${color};display:flex;align-items:center;justify-content:center;border:2px solid #fff;${ring}opacity:${opacity};transition:opacity 0.15s;cursor:pointer;">
      ${glyphSvg(glyph, { size: opts.compact ? 12 : 15, fg: "#fff", bg: color })}
    </div>
  `;
  const half = Math.round(size / 2);
  return { content, anchorX: half, anchorY: half, zIndex: z, tooltip: orgTooltip(o, colorKey, glyph, opts.note) };
}

// "우리 기관" — agency 대시보드 미니맵의 소속 기관 위치 표식. 일반 기관 마커와
// 헷갈리지 않도록 브랜드 오렌지 + 글로우 링으로 한 단계 강조한다.
export function homeOrgMarkerStyle(o: any, opts: MarkerSpec = {}): MarkerStyle {
  const color = PALETTE.orange[500];
  const size = opts.compact ? 26 : 32;
  const content = `
    <div style="width:${size}px;height:${size}px;border-radius:9px;background:${color};display:flex;align-items:center;justify-content:center;border:2px solid #fff;${SELECTED_RING}cursor:default;">
      ${glyphSvg("care", { size: opts.compact ? 14 : 17, fg: "#fff", bg: color })}
    </div>
  `;
  const half = Math.round(size / 2);
  return {
    content, anchorX: half, anchorY: half, zIndex: Z_SELECTED,
    tooltip: tooltipCard({
      accent: color, glyph: "care",
      title: o.name || "우리 기관", sub: "소속 기관",
      badges: [{ label: "우리 기관", color: "orange" }],
      rows: o.address ? [["주소", o.address]] : [],
    }),
  };
}

// 선택 상태 글로우: outline 4px orange + drop shadow. CSS box-shadow ring 이중겹.
const SELECTED_RING =
  "box-shadow:0 0 0 4px rgba(232,130,91,0.55),0 6px 14px rgba(0,0,0,0.22);";

// 검색 미매치 dim 정도. map.tsx의 DIM_OPACITY와 동일.
const DIM_OPACITY = 0.25;

// 마커 종류별 기본 zIndex. 시안 의도: 어르신을 위에, 기관을 아래로.
// 선택된 마커는 항상 최상단. 클러스터는 종 단위 zIndex와 같게 두어 같은 셀에
// 여러 종 클러스터가 있을 때 종별 우선순위가 유지되도록.
const Z_ORG      = 10;
const Z_ELDERLY  = 50;
const Z_MANAGER  = 30;
const Z_SELECTED = 1000;

// ── Cluster marker ─────────────────────────────────────────────────

// 종별 클러스터 색. 단일 색만 사용 — 클러스터 안의 카테고리/위험등급 다양성은
// 줌인으로 분해되며 드러난다.
const CLUSTER_COLOR: Record<EntityKind, { hex: string; hexDark: string }> = {
  elderly: { hex: PALETTE.rose[500],   hexDark: PALETTE.rose[600] },
  manager: { hex: PALETTE.orange[500], hexDark: PALETTE.orange[600] },
  org:     { hex: PALETTE.sky[500],    hexDark: PALETTE.sky[600] },
};

// 클러스터도 어떤 종의 묶음인지 한눈에 보이도록 개별 마커와 같은 픽토그램을 쓴다.
const CLUSTER_GLYPH: Record<EntityKind, GlyphKey> = {
  elderly: "elderly", manager: "manager", org: "welfare",
};

// count에 따라 시각 무게를 키운다 — 사용자가 한눈에 군집 크기를 인지할 수 있게.
function clusterSize(count: number): number {
  if (count >= 100) return 60;
  if (count >= 50)  return 52;
  if (count >= 10)  return 44;
  return 36;
}

// 클러스터 마커 — 종별 색 둥근 원 + 가운데 count + 위쪽 작은 픽토그램.
export function clusterMarkerStyle(kind: EntityKind, count: number): MarkerStyle {
  const c    = CLUSTER_COLOR[kind];
  const size = clusterSize(count);
  const fontSize = count >= 100 ? 15 : 13;
  const glyphSize = size >= 52 ? 13 : size >= 44 ? 12 : 10;

  const content = `
    <div style="width:${size}px;height:${size}px;border-radius:50%;background:linear-gradient(135deg,${c.hex},${c.hexDark});color:#fff;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1px;font-weight:700;font-size:${fontSize}px;line-height:1;border:3px solid #fff;box-shadow:0 6px 18px rgba(0,0,0,0.28);cursor:pointer;">
      <span style="opacity:0.85;">${glyphSvg(CLUSTER_GLYPH[kind], { size: glyphSize, fg: "#fff", bg: c.hexDark })}</span>
      <span>${count}</span>
    </div>
  `;

  const anchor = Math.round(size / 2) + 3 /* border */;
  const z = kind === "elderly" ? Z_ELDERLY : kind === "manager" ? Z_MANAGER : Z_ORG;
  return {
    content, anchorX: anchor, anchorY: anchor, zIndex: z,
    tooltip: tooltipCard({
      accent: c.hex,
      glyph: CLUSTER_GLYPH[kind],
      title: `${KIND_LABEL[kind]} ${count.toLocaleString()}${kind === "org" ? "곳" : "명"}`,
      sub: "이 위치에 겹친 마커 묶음",
      badges: [],
      rows: [],
      hint: "클릭하면 확대해서 펼칩니다",
    }),
  };
}

// 공단평가 등급별 색 — A(우수)에서 E(미흡)로 갈수록 경고 톤.
const NHIS_GRADE_COLOR: Record<string, ColorKey> = {
  A: "green", B: "sky", C: "amber", D: "orange", E: "rose",
};

// 입소율이 높을수록 "받을 자리가 없다"는 신호라 경고 톤으로.
function occupancyColor(capacity: number, occupancy: number | null | undefined): ColorKey {
  if (occupancy == null || capacity <= 0) return "sky";
  const rate = occupancy / capacity;
  return rate >= 0.95 ? "rose" : rate >= 0.8 ? "amber" : "green";
}

// 분 단위 지표를 사람이 읽는 단위로. 케어 응답은 분~일 단위로 폭이 넓다.
function formatMins(mins: number): string {
  if (mins < 60) return `${Math.round(mins)}분`;
  if (mins < 60 * 24) return `${(mins / 60).toFixed(1)}시간`;
  return `${(mins / 1440).toFixed(1)}일`;
}

// ── Tooltip content ────────────────────────────────────────────────

interface Badge { label: string; color: ColorKey }
// [라벨, 값] 또는 [라벨, 값, 강조색]. 세 번째를 주면 값이 그 색 + 굵게 렌더된다 —
// 운영 지표(매니저 수·케이스·응답시간)처럼 "읽고 판단하는 숫자"를 도드라지게.
type Row = [string, string] | [string, string, ColorKey];

// 툴팁 카드 한 장. 어두운 유리판 위에 [아이콘칩 + 제목/종류] → 배지 → 메타행 순서.
// 인라인 style만 쓴다 — body 직속으로 붙어 .app의 CSS 상속을 받지 못하기 때문.
function tooltipCard(o: {
  accent: string;
  glyph: GlyphKey;
  title: string;
  sub: string;
  badges: Badge[];
  rows: Row[];
  hint?: string;
}): string {
  const badges = o.badges.length === 0 ? "" : `
    <div style="display:flex;flex-wrap:wrap;gap:4px;margin-top:8px;">
      ${o.badges.map(b => `<span style="font-size:10px;font-weight:700;line-height:1.6;padding:1px 8px;border-radius:999px;background:rgba(255,255,255,0.12);color:${PALETTE[b.color][300]};">${escape(b.label)}</span>`).join("")}
    </div>`;

  const rows = o.rows.length === 0 ? "" : `
    <div style="margin-top:8px;padding-top:7px;border-top:1px solid rgba(255,255,255,0.13);display:flex;flex-direction:column;gap:3px;">
      ${o.rows.map(([k, v, c]) => `
        <div style="display:flex;gap:8px;font-size:11px;line-height:1.5;">
          <span style="flex:none;width:56px;color:rgba(255,255,255,0.5);">${escape(k)}</span>
          <span style="min-width:0;flex:1;text-align:${c ? "right" : "left"};font-weight:${c ? 700 : 400};color:${c ? PALETTE[c][300] : "rgba(255,255,255,0.94)"};word-break:break-word;">${escape(v)}</span>
        </div>`).join("")}
    </div>`;

  const hint = !o.hint ? "" :
    `<div style="margin-top:7px;font-size:10px;color:rgba(255,255,255,0.45);">${escape(o.hint)}</div>`;

  return `
    <div style="display:flex;align-items:center;gap:9px;">
      <span style="flex:none;width:28px;height:28px;border-radius:9px;background:${o.accent};display:flex;align-items:center;justify-content:center;">
        ${glyphSvg(o.glyph, { size: 16, fg: "#fff", bg: o.accent })}
      </span>
      <span style="min-width:0;">
        <span style="display:block;font-size:13px;font-weight:700;color:#fff;line-height:1.3;">${escape(o.title)}</span>
        <span style="display:block;font-size:10.5px;color:rgba(255,255,255,0.55);line-height:1.4;">${escape(o.sub)}</span>
      </span>
    </div>${badges}${rows}${hint}`;
}

function elderlyTooltip(e: any, colorKey: ColorKey, note?: string): string {
  const badges: Badge[] = [];
  if (note) badges.push({ label: note, color: "orange" });
  if (RISK_LABEL[e.risk_level]) badges.push({ label: RISK_LABEL[e.risk_level], color: colorKey });
  if (e.active_case_id) badges.push({ label: "매칭 대기", color: "orange" });
  if (e.sub_area) badges.push({ label: e.sub_area, color: "gray" });

  const rows: Row[] = [];
  if (e.age != null) rows.push(["나이", `${e.age}세`]);
  if (e.health_status) rows.push(["건강", e.health_status]);
  if (e.address) rows.push(["주소", e.address]);

  return tooltipCard({
    accent: PALETTE[colorKey][500],
    glyph: "elderly",
    // 비로그인(공개 랜딩)에는 이름이 내려오지 않는다 — 종류 라벨로 대체.
    title: e.name || "어르신",
    sub: KIND_LABEL.elderly,
    badges, rows,
  });
}

function managerTooltip(m: any, dotKey: ColorKey, note?: string): string {
  const badges: Badge[] = [];
  if (note) badges.push({ label: note, color: "orange" });
  if (MANAGER_STATUS_LABEL[m.status]) badges.push({ label: MANAGER_STATUS_LABEL[m.status], color: dotKey });
  if (m.assigned_cases != null) badges.push({ label: `담당 ${m.assigned_cases}건`, color: "orange" });
  if (m.rating != null) badges.push({ label: `★ ${Number(m.rating).toFixed(1)}`, color: "amber" });

  const rows: Row[] = [];
  if (m.organization_name) rows.push(["소속", m.organization_name]);
  if (m.qualification) rows.push(["자격", m.qualification]);

  return tooltipCard({
    accent: PALETTE.orange[500],
    glyph: "manager",
    title: m.name || "케어매니저",
    sub: KIND_LABEL.manager,
    badges, rows,
  });
}

function orgTooltip(o: any, colorKey: ColorKey, glyph: GlyphKey, note?: string): string {
  const badges: Badge[] = [];
  if (note) badges.push({ label: note, color: "orange" });
  if (CATEGORY_LABEL[o.category]) badges.push({ label: CATEGORY_LABEL[o.category], color: colorKey });
  if (CONTRACT_LABEL[o.contract_status]) {
    badges.push({ label: CONTRACT_LABEL[o.contract_status], color: CONTRACT_COLOR[o.contract_status] ?? "gray" });
  }

  // 순서는 gcare 지역지도 카드와 동일 — 시설 평가/규모를 먼저, 운영 지표, 연락처 순.
  // 값이 없는 지표는 줄 자체를 빼서 빈칸이 카드를 늘리지 않게 한다(임포트 전 기관은
  // 정원·공단평가가 비어 있다).
  const rows: Row[] = [];
  if (o.nhis_grade || o.nhis_score != null) {
    const g = o.nhis_grade ? String(o.nhis_grade) : null;
    const s = o.nhis_score != null ? `${Number(o.nhis_score).toFixed(1)}점` : null;
    rows.push(["공단평가", [g, s].filter(Boolean).join(" · "), NHIS_GRADE_COLOR[g ?? ""] ?? "sky"]);
  }
  if (o.manager_rating != null && o.manager_rating > 0) {
    rows.push(["매니저 평점", `★ ${Number(o.manager_rating).toFixed(1)}`, "amber"]);
  }
  if (o.staff_count != null && o.staff_count > 0) {
    rows.push(["직원", `${o.staff_count}명`, "sky"]);
  }
  if (o.capacity != null && o.capacity > 0) {
    const occ = o.current_occupancy != null ? o.current_occupancy : "-";
    rows.push(["정원/현원", `${o.capacity}/${occ}명`, occupancyColor(o.capacity, o.current_occupancy)]);
  }
  if (o.manager_count != null && o.manager_count > 0) {
    rows.push(["소속 매니저", `${o.manager_count}명`, "orange"]);
  }
  if (o.case_count != null && o.case_count > 0) {
    rows.push(["담당 케이스", `${o.case_count}건`, "sky"]);
  }
  if (o.avg_response_mins != null && o.avg_response_mins > 0) {
    rows.push(["평균 응답", formatMins(o.avg_response_mins), "green"]);
  }
  if (o.contact_name) {
    rows.push(["담당자", o.contact_title ? `${o.contact_name} ${o.contact_title}` : o.contact_name]);
  }
  if (o.phone) rows.push(["연락처", o.phone]);
  if (o.address) rows.push(["주소", o.address]);

  // gcare 지역지도의 "요양시설 · 동구권역"처럼 종류 + 권역을 부제로.
  const cat = CATEGORY_LABEL[o.category];
  const sub = [cat || KIND_LABEL.org, o.region_name].filter(Boolean).join(" · ");

  return tooltipCard({
    accent: PALETTE[colorKey][500],
    glyph,
    title: o.name || "유관기관",
    sub,
    badges, rows,
  });
}

// HTML content에 들어가는 사용자 텍스트를 escape — 이름이 직접 innerHTML로
// 들어가므로 XSS 방지 (백엔드 데이터지만 정책상 보수적으로).
function escape(s: string): string {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
